import request from 'lib/request';
import { US_DATE_FORMAT, parseMomentUtc } from 'utils/dates';

/** What the catalog and the detail page render for a value they don't have. */
export const EMPTY_CELL = '—';

/** The two flags an area's lifecycle is expressed in. */
export interface AreaFlags {
  shared: boolean;
  active: boolean;
}

interface UpdatedArea extends AreaFlags {
  id: number;
}

interface ErrorEnvelope {
  errors?: Array<{ message?: string }>;
}

/** Either the flags the backend settled on, or why it refused. */
export type AreaFlagsResult =
  { ok: true; flags: AreaFlags } | { ok: false; message: string };

/**
 * A timestamp as the catalog shows it — read as UTC, since that is what the API
 * sends and the browser's zone would otherwise move the date.
 *
 * @param {string | undefined} value - ISO timestamp from the API.
 * @return {string} The formatted date, or the empty marker.
 */
export const formatDate = (value: string | undefined) => {
  return value ? parseMomentUtc(value).format(US_DATE_FORMAT) : EMPTY_CELL;
};

/**
 * Writes an area's lifecycle flags: `shared` puts it on Insights maps (and routes
 * a Unit into development areas), `active` archives it. The response is the
 * authority — archiving clears `shared` server-side.
 *
 * @param {number | string} id - The area being changed; table rows carry it as a
 * string.
 * @param {Partial<AreaFlags>} flags - Only the flags being changed.
 * @return {Promise<AreaFlagsResult>} The settled flags, or the refusal message.
 */
export const patchAreaFlags = async (
  id: number | string,
  flags: Partial<AreaFlags>
): Promise<AreaFlagsResult> => {
  const response = await request.patch<UpdatedArea, ErrorEnvelope>(
    `/area_geometries/${id}`,
    flags,
    new AbortController()
  );

  if (response.type === 'success') {
    return {
      ok: true,
      flags: { shared: response.data.shared, active: response.data.active },
    };
  }

  const message =
    response.type === 'error'
      ? response.data?.errors?.[0]?.message
      : response.message;
  return { ok: false, message: message ?? 'Could not update the area.' };
};
